import { Card } from "@/ingot";
import { useTranslation } from "react-i18next";

import type { AnalyzerWarning } from "@/api/types";

import { Pill } from "@/components/ui/Pill";

import type {
  ManufacturabilityWarning,
  RepairChange,
} from "@/api/queries/quotes";
import { isBlockingSeverity } from "@/lib/manufacturabilitySeverity";

interface Props {
  warnings: AnalyzerWarning[];
  status: string;
  /**
   * Manufacturability warnings from
   * `GET /quotes/{slug}/manufacturability` — populated once the
   * configurator picks material + variant. The configurator passes
   * these in; QuoteDetail / preview-only paths pass an empty array.
   */
  manufacturabilityWarnings?: ManufacturabilityWarning[];
  /**
   * `true` when the latest manufacturability check returned
   * `blocking=true`. The panel renders the "Vyrobitelnost" section
   * in danger tone and the caller (configurator) disables submit.
   */
  blocking?: boolean;
  /**
   * Applied fixes from a `RepairedGeometry` row (geometry hygiene
   * close-gaps + per-process manufacturability auto-fixes once the
   * customer clicks "Opravit automaticky"). Renders the "Opraveno"
   * section.
   */
  appliedFixes?: RepairChange[];
  /**
   * KAN-1639 — "Rozdělit na díly". Offered on a warning that says the file
   * holds several parts (the analyzer marks it with ``params.parts_fact``,
   * the output carrying the count). Omitted = no split action on this surface.
   */
  onSplitParts?: () => void;
  splitPending?: boolean;
  splitError?: boolean;
}

/**
 * Validation panel for the configurator page — three semantic sections:
 *
 *   1. **Opraveno** — geometry hygiene + manufacturability fixes already
 *      applied to the manufacturing copy (only renders when the customer
 *      gave consent to `POST /auto-repair-manufacturability`).
 *   2. **Varování** — process-agnostic analyzer warnings + non-blocking
 *      manufacturability warnings (TINY_HOLE on parts the customer
 *      hasn't yet picked a variant for).
 *   3. **Vyrobitelnost** — blocking warnings (hole-to-axis ratio
 *      fails for the picked material). Red tone; the configurator
 *      disables "Continue" while at least one of these is present.
 *
 * Each warning code falls through three i18n keys:
 *   1. `quote:warnings.codes.<code>` — the human-readable message
 *      with `{{param}}` interpolation;
 *   2. `quote:warnings.codes.fallback` — a plain generic sentence for
 *      codes we haven't translated yet (never the raw code, KAN-1864).
 *
 * Backend warning catalog is in
 * `packages/analyzer/src/dxf_analyzer/warnings.py` (process-agnostic)
 * + `apps/api/app/services/processes/manufacturability.py`
 * (per-process). Codes are append-only — never repurpose.
 */
const MANUFACTURABILITY_WARNING_CODES = new Set([
  "tiny_hole",
  "thin_segment",
  // KAN-1338 — the part does not fit the operation's equipment.
  "part_too_long",
  "part_too_wide",
  // KAN-1402 — the part's material does not suit a further operation.
  "material_category_not_allowed",
  "material_property_not_allowed",
  "hole_diameter_below_axis_ratio",
  "slot_width_too_narrow",
  "corner_radius_too_sharp",
  "bridge_width_insufficient",
  // Declarative limit constraint violation (manufacturability.py generic pass)
  // — a blocking warning, so it must be in this set to render in the blocking
  // section instead of being filtered out.
  "limit_exceeded",
]);

export function WarningsPanel({
  warnings: allWarnings,
  status,
  manufacturabilityWarnings: manufIn = [],
  blocking = false,
  appliedFixes = [],
  onSplitParts,
  splitPending = false,
  splitError = false,
}: Props): JSX.Element | null {
  const { t, i18n } = useTranslation("quote");

  // KAN-1790 — one message per problem. An analyzer finding that names the
  // fact it explains (``params.explains_fact``, e.g. an open contour with its
  // place and gap) REPLACES the generic "limit exceeded" record on that same
  // fact: the specific text says where and how big, the limit text only says
  // that something is wrong. Where the limit blocks, the specific finding
  // takes over the blocking section.
  const factsOf = (w: { params?: Record<string, unknown> }): string[] => {
    const raw = w.params?.explains_fact;
    if (typeof raw === "string") return [raw];
    return Array.isArray(raw) ? raw.filter((f): f is string => typeof f === "string") : [];
  };
  const explainedFacts = new Set(allWarnings.flatMap(factsOf));
  const isExplainedLimit = (w: { code: string; params?: Record<string, unknown> }): boolean =>
    w.code === "limit_exceeded" &&
    typeof w.params?.parameter === "string" &&
    explainedFacts.has(w.params.parameter);
  const explainedBlocks = [...manufIn, ...allWarnings].some(
    (w) => isExplainedLimit(w) && isBlockingSeverity(w.params?.severity ?? "block"),
  );
  const openContourIds = new Set(
    allWarnings.filter((w) => factsOf(w).length > 0).map((w) => w.params?.component_id),
  );
  const manufacturabilityWarnings = manufIn.filter((w) => !isExplainedLimit(w));
  const warnings = allWarnings.filter(
    (w) =>
      !isExplainedLimit(w) &&
      // The "nearly closed" note repeats an open contour that already
      // carries its own place and gap.
      !(w.code === "component_nearly_closed" && openContourIds.has(w.params?.component_id)),
  );

  // KAN-1639 — "the file holds N parts" is ONE message whatever analyzer said
  // it: the analyzer names the fact carrying the count (``parts_fact``), so no
  // code is listed here. When a data limit on that same fact blocks, the block
  // is the only thing the customer should read — the "you can split it" note
  // next to "not allowed" contradicted it (v59, test11).
  const partsFactBlocked = new Set(
    [...manufacturabilityWarnings, ...warnings]
      .filter(
        (w) =>
          w.code === "limit_exceeded" &&
          isBlockingSeverity(w.params?.severity ?? "block") &&
          typeof w.params?.parameter === "string",
      )
      .map((w) => w.params?.parameter as string),
  );
  const partsWarnings: AnalyzerWarning[] = [];

  // Classify analyzer warnings into manufacturability vs generic.
  // Analyzer codes like `tiny_hole` + `thin_segment` may live on the
  // analyzer side; they still belong in the manufacturability bucket
  // semantically.
  const genericWarnings: AnalyzerWarning[] = [];
  const analyzerManufWarnings: AnalyzerWarning[] = [];
  // The upload stamps its manufacturability result into the analysis warnings
  // and the live check returns the same records (KAN-1298) — show each once,
  // in the live check's section.
  const liveManufKeys = new Set(manufacturabilityWarnings.map(warningKey));
  // KAN-1638 — a finding the analyzer itself marks ``severity: block`` (part
  // of the drawing could not be read, so a price would come from part of the
  // outline) stops the quote: it belongs with the blockers, whatever its code.
  const analyzerBlocking: AnalyzerWarning[] = [];
  for (const w of warnings) {
    if (factsOf(w).length > 0) {
      if (explainedBlocks) analyzerBlocking.push(w);
      else genericWarnings.push(w);
      continue;
    }
    const partsFact = w.params?.parts_fact;
    if (typeof partsFact === "string") {
      if (!partsFactBlocked.has(partsFact) && partsWarnings.length === 0) {
        partsWarnings.push(w);
      }
      continue;
    }
    if (w.params?.severity === "block") {
      if (!liveManufKeys.has(warningKey(w))) analyzerBlocking.push(w);
      continue;
    }
    if (MANUFACTURABILITY_WARNING_CODES.has(w.code)) {
      if (liveManufKeys.has(warningKey(w))) continue;
      analyzerManufWarnings.push(w);
    } else {
      genericWarnings.push(w);
    }
  }

  // KAN-1532 — section by the WARNING's severity, not by the check's overall
  // verdict. ``blocking`` is one flag for the whole check; using it to classify
  // every warning put a warn-only finding into the red "blokuje pokračování"
  // section and counted it as a second problem beside the one that actually
  // blocks. The backend stamps ``params.severity`` from the limit binding
  // (same convention ``limit_exceeded`` already used); a warning without one —
  // an older snapshot, another producer — keeps the old behaviour and follows
  // the overall flag, so nothing silently changes tone.
  const blockingWarnings: (ManufacturabilityWarning | AnalyzerWarning)[] = [
    ...analyzerBlocking,
  ];
  const nonBlockingManuf: ManufacturabilityWarning[] = [];
  for (const w of manufacturabilityWarnings) {
    const severity = w.params?.severity;
    const isBlocking =
      typeof severity === "string" ? isBlockingSeverity(severity) : blocking;
    // A code this panel does not know must not be painted as a blocker — it
    // has no place in the red section. It still gets shown, which is the
    // point: before KAN-1532 a blocking check dropped it on the floor.
    if (isBlocking && MANUFACTURABILITY_WARNING_CODES.has(w.code)) {
      blockingWarnings.push(w);
    } else {
      nonBlockingManuf.push(w);
    }
  }

  const hasAnyWarnings =
    partsWarnings.length +
    analyzerBlocking.length +
      genericWarnings.length +
      analyzerManufWarnings.length +
      manufacturabilityWarnings.length +
      appliedFixes.length >
    0;

  if (!hasAnyWarnings && status === "OK") {
    return (
      <Card padded className="border-ok-border bg-ok-bg" data-testid="warnings-ok">
        <div className="flex items-center gap-3">
          <Pill tone="ok" icon={<span aria-hidden="true">✓</span>}>
            {t("warnings.statusOk")}
          </Pill>
          <p className="text-sm text-ink-2">{t("warnings.bodyOk")}</p>
        </div>
      </Card>
    );
  }

  const sectionPadding = "px-4 py-3";
  const headerPadding = "px-4 py-2.5";

  return (
    <Card padded={false} data-testid="warnings-panel" className="border-warn-border">
      {/* Section 1 — Opraveno (applied fixes from RepairedGeometry) */}
      {appliedFixes.length > 0 && (
        <section data-testid="warnings-section-fixed" className="border-b border-border">
          <header className={`flex items-center justify-between gap-3 ${headerPadding}`}>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">
                {t("warnings.section.fixed", { defaultValue: "Opraveno" })}
              </h3>
              <Pill tone="ok">
                {t("warnings.fixCount", {
                  defaultValue: "{{count}} úprava",
                  defaultValue_other: "{{count}} úprav",
                  count: appliedFixes.length,
                })}
              </Pill>
            </div>
          </header>
          <ul className="divide-y divide-border">
            {appliedFixes.map((fix, i) => (
              <li key={`${fix.kind}-${i}`} className={sectionPadding}>
                <div className="rounded-md border-l-4 border-ok bg-ok-bg/40 px-3 py-2">
                  <p className="text-sm font-medium text-ok">
                    {t(`warnings.fixes.${fix.kind}`, {
                      ...formatParams(fix.params, i18n.language),
                      defaultValue: t("warnings.fixes.fallback", {
                        defaultValue: `Oprava: ${fix.kind}`,
                        kind: fix.kind,
                      }),
                    })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Section 2 — Vyrobitelnost (blocking) */}
      {blockingWarnings.length > 0 && (
        <section
          data-testid="warnings-section-blocking"
          className="border-b border-border"
        >
          <header
            className={`flex items-center justify-between gap-3 ${headerPadding}`}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">
                {t("warnings.section.blocking", {
                  defaultValue: "Vyrobitelnost",
                })}
              </h3>
              <Pill tone="danger">
                {t("warnings.problemCount", {
                  count: blockingWarnings.length,
                })}
              </Pill>
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
              {t("warnings.section.blockingHint", {
                defaultValue: "blokuje pokračování",
              })}
            </span>
          </header>
          <ul className="divide-y divide-border">
            {blockingWarnings.map((w, i) => (
              <WarningChip
                key={`blk-${w.code}-${i}`}
                warning={w}
                tone="danger"
                translate={t}
                lang={i18n.language}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Section 3 — Varování (non-blocking) */}
      {(partsWarnings.length +
        genericWarnings.length +
        analyzerManufWarnings.length +
        nonBlockingManuf.length >
        0 ||
        status === "ERROR") && (
        <section data-testid="warnings-section-generic">
          <header
            className={`flex items-center justify-between gap-3 ${headerPadding}`}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">
                {t("warnings.section.generic", {
                  defaultValue: "Varování",
                })}
              </h3>
              <Pill tone={status === "ERROR" ? "danger" : "warn"}>
                {t("warnings.problemCount", {
                  count:
                    partsWarnings.length +
                    genericWarnings.length +
                    analyzerManufWarnings.length +
                    nonBlockingManuf.length,
                })}
              </Pill>
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
              {t("warnings.foundLabel")}
            </span>
          </header>
          <ul className="divide-y divide-border">
            {partsWarnings.map((w) => (
              <li key={`parts-${w.code}`} className="px-4 py-3" data-testid="warnings-parts">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border-l-4 border-border bg-surface-2 px-3 py-2">
                  <p className="text-sm font-medium text-ink">
                    {t("warnings.parts.info", {
                      count: typeof w.params?.count === "number" ? w.params.count : 2,
                    })}
                  </p>
                  {onSplitParts && (
                    <button
                      type="button"
                      className="text-sm font-semibold text-ink underline underline-offset-2 disabled:opacity-60"
                      onClick={onSplitParts}
                      disabled={splitPending}
                      data-testid="warnings-parts-split"
                    >
                      {splitPending ? t("warnings.parts.splitting") : t("warnings.parts.split")}
                    </button>
                  )}
                </div>
                {splitError && (
                  <p className="mt-2 text-sm text-danger" role="alert">
                    {t("warnings.parts.splitFailed")}
                  </p>
                )}
              </li>
            ))}
            {genericWarnings.map((w, i) => (
              <WarningChip
                key={`gen-${w.code}-${i}`}
                warning={w}
                tone={status === "ERROR" ? "danger" : "warn"}
                translate={t}
                lang={i18n.language}
              />
            ))}
            {analyzerManufWarnings.map((w, i) => (
              <WarningChip
                key={`amf-${w.code}-${i}`}
                warning={w}
                tone="warn"
                translate={t}
                lang={i18n.language}
              />
            ))}
            {nonBlockingManuf.map((w, i) => (
              <WarningChip
                key={`nbm-${w.code}-${i}`}
                warning={w}
                tone="warn"
                translate={t}
                lang={i18n.language}
              />
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}

function WarningChip({
  warning: w,
  tone,
  translate: t,
  lang,
}: {
  warning: AnalyzerWarning | ManufacturabilityWarning;
  tone: "danger" | "warn";
  translate: (key: string, opts?: Record<string, unknown>) => string;
  lang: string;
}): JSX.Element {
  const isDanger = tone === "danger";
  const headline = renderMessage(w, t, lang);
  return (
    <li className="px-4 py-3">
      <div
        className={`rounded-md border-l-4 px-3 py-2 ${
          isDanger
            ? "border-danger bg-danger-bg/40"
            : "border-warn bg-warn-bg/40"
        }`}
      >
        <p
          className={`text-sm font-medium ${isDanger ? "text-danger" : "text-warn"}`}
        >
          {headline}
        </p>
      </div>
    </li>
  );
}

/** KAN-1650 — tentýž text důvodu jako zákazník vidí v konfigurátoru; čte ho
 *  i obchod v detailu poptávky. */
export function renderMessage(
  w: AnalyzerWarning | ManufacturabilityWarning,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
): string {
  // i18next returns the key itself when no translation exists; we
  // detect that and fall through to the generic template.
  // KAN-1220 — a violated declarative limit that carries its authored name
  // reads as that name, not as the raw analyzer key + operator.
  // KAN-1300 — a ``warn`` binding must not tell the customer the quote is
  // stopped, and an untitled one reads as a plain sentence, not raw keys.
  // KAN-1345 — the mesh analyzer emits its codes upper-case (``HEAVY_OVERHANG``)
  // while the catalog keys are lower-case, so every mesh warning used to fall
  // through to the raw "Detekováno: HEAVY_OVERHANG".
  let key = `warnings.codes.${w.code.toLowerCase()}`;
  // KAN-1790 — an open contour with several loose ends has no single gap.
  if (w.code === "open_contour" && w.params?.gap === undefined) key += "_nogap";
  if (w.code === "limit_exceeded") {
    const titled = typeof w.params?.limit_title === "string";
    const warnOnly = w.params?.severity === "warn";
    // KAN-1790 — logická podmínka (``actual`` = true/false) nemá číslo, které by
    // dávalo smysl psát; zákazník nečte „zjištěno false“.
    const actual = w.params?.actual;
    const noValue = typeof actual === "boolean" || actual === null || actual === undefined;
    key = `warnings.codes.limit_exceeded${titled ? (noValue ? "_flag" : "_titled") : ""}${warnOnly ? "_warn" : ""}`;
    // KAN-1864 — a limit over a counted fact the catalog can describe reads
    // as what is wrong and how to fix it ("Výkres obsahuje 1 neuzavřenou
    // konturu … Spojte konce čar v CADu"), not as "{title}: soubor tuhle
    // podmínku nesplňuje (zjištěno 1)". Keyed by the fact the limit binds
    // (``params.parameter``) — data, no process or limit is named here.
    const parameter = w.params?.parameter;
    if (typeof parameter === "string" && typeof actual === "number") {
      const fact = t(`warnings.facts.${parameter}`, { count: actual, defaultValue: "" });
      if (fact) {
        return `${fact} ${t(`warnings.limitConsequence.${warnOnly ? "warn" : "block"}`)}`;
      }
    }
  }
  const translated = t(key, { ...formatParams(w.params ?? {}, lang, t), defaultValue: "" });
  if (translated) return translated;
  // KAN-1864 — a code the catalog does not know yet reads as a plain
  // sentence; the internal code never reaches the customer.
  return t("warnings.codes.fallback");
}

function warningKey(w: AnalyzerWarning | ManufacturabilityWarning): string {
  // KAN-1532 — identity of the FINDING, not of the message. The upload stamps
  // its manufacturability result into the analysis warnings and the live check
  // re-runs the same rules (KAN-1298), but the two runs resolve their
  // thresholds independently: the moment a limit moves between upload and
  // configure, whole-params equality stops matching and the customer sees one
  // hole described twice, once per section. The geometry the warning points at
  // is what makes two records the same finding.
  // The analyzer's own ``tiny_hole`` names the hole by ``component_id`` only,
  // the platform's by ``loop_id`` too — so the component comes first.
  const params = (w.params ?? {}) as Record<string, unknown>;
  const at = params.component_id ?? params.loop_id ?? params.corner_id;
  if (at !== undefined && at !== null) return JSON.stringify([w.code, at]);
  // KAN-1630 — a violated limit IS its limit (per operation). Comparing the
  // whole params never matched: the upload copy comes back from a Postgres
  // JSONB column, which reorders object keys, so one open mesh read as two
  // identical blockers, one per section.
  if (w.code === "limit_exceeded" && params.limit_key !== undefined) {
    return JSON.stringify([w.code, params.limit_key, params.process_type ?? null]);
  }
  return JSON.stringify([w.code, sortedEntries(params)]);
}

/** ``params`` as key-sorted pairs — equal records compare equal whatever
 *  order their keys were stored in. */
function sortedEntries(params: Record<string, unknown>): [string, unknown][] {
  return Object.keys(params)
    .sort()
    .map((k) => [k, params[k]]);
}

export function formatParams(
  params: Record<string, unknown>,
  lang: string,
  t?: (key: string, opts?: Record<string, unknown>) => string,
): Record<string, unknown> {
  // Floats render with 2 decimals in cs (1,23) / en (1.23). Other types
  // pass through unchanged so `{count}` ints stay clean.
  // KAN-1638 — a value the analyzer reports as a word (``inches``,
  // ``meters``, ``unitless``) is read through ``warnings.values`` so no English
  // token lands in a Czech sentence; a list (``entity_types``) reads as a list.
  const fmt = new Intl.NumberFormat(lang === "cs" ? "cs-CZ" : "en-US", {
    maximumFractionDigits: 2,
  });
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === "number" && !Number.isInteger(v)) {
      out[k] = fmt.format(v);
    } else if (typeof v === "string" && t) {
      out[k] = t(`warnings.values.${v}`, { defaultValue: v });
    } else if (Array.isArray(v)) {
      out[k] = v.map(String).join(", ");
    } else {
      out[k] = v;
    }
  }
  return out;
}
